import { addonSetUserId, addonTrack } from './addonClient';
import { getState } from '../../spa/store';
import { getAuthUser } from '../../spa/store/general/selectors';
import { cachedRoomHash, roomHash } from './roomHash';

// События звонка уходят только в наш экземпляр Amplitude (не через EventsRouter).
// Безопасные свойства: room_hash (SHA-256 имени комнаты, 16 символов — само имя это секрет
// приглашения и не уходит), target_lang, participants, duration_sec, provider.
// Каждый вызов — fire-and-forget, ошибки не выходят наружу.

const safe = (fn: () => void): void => {
  try { fn(); } catch (e) { /* noop */ }
};

// Привязываем события звонка к hashId текущего пользователя (только id, без email/имени).
const bindUser = (): void => safe(() => {
  const user = getAuthUser(getState());
  if (user?.hashId) addonSetUserId(user.hashId);
});

// Хеш считается асинхронно; событие уходит, когда он готов (не получился — без поля).
const withRoomHash = (roomName: string, fn: (hash: string | undefined) => void): void => safe(() => {
  roomHash(roomName)
    .then(h => safe(() => fn(h || undefined)))
    .catch(() => safe(() => fn(undefined)));
});

export const trackRoomCreated = (roomId: string, targetLang?: string, multilingual?: boolean): void => safe(() => {
  bindUser();
  withRoomHash(roomId, (hash) => {
    addonTrack('room_created', { room_hash: hash, target_lang: targetLang, multilingual: !!multilingual });
  });
});

export const trackInviteCopied = (): void => safe(() => addonTrack('invite_copied', {}));

// Подписывается на события Daily call object: join / leave / участники / первая подпись.
export const attachCallTracking = (callObject: any): void => safe(() => {
  if (!callObject || typeof callObject.on !== 'function') return;

  let joiningAt = 0;
  let joinedAt = 0;
  let connectedSent = false;
  let maxParticipants = 0;
  let captionsSent = false;
  let roomName = '';
  let hash = '';

  const countParticipants = (): number => {
    try {
      return Object.keys(callObject.participants() || {}).length;
    } catch (e) { return 0; }
  };
  const bump = () => {
    const n = countParticipants();
    if (n > maxParticipants) maxParticipants = n;
  };
  const readRoomName = (): string => {
    try {
      return String((callObject.properties?.url || '').split('/').pop() || '');
    } catch (e) { return ''; }
  };

  callObject.on('joining-meeting', () => safe(() => {
    joiningAt = Date.now();
    connectedSent = false;
    // считаем хеш заранее, чтобы call_ended на закрытии вкладки ушёл синхронно уже с ним
    roomName = readRoomName();
    roomHash(roomName).then(h => { if (h) hash = h; }).catch(() => { /* noop */ });
  }));

  callObject.on('joined-meeting', () => safe(() => {
    joinedAt = Date.now();
    bindUser();
    bump();
    roomName = readRoomName() || roomName;
    if (!connectedSent) {
      connectedSent = true;
      addonTrack('call_connected', {
        ms_to_connect: joiningAt ? joinedAt - joiningAt : undefined,
        participants_now: countParticipants(),
        provider: 'daily',
      });
    }
    const participants = maxParticipants;
    withRoomHash(roomName, (h) => {
      if (h) hash = h;
      addonTrack('call_joined', { room_hash: h, participants, provider: 'daily' });
    });
  }));
  callObject.on('participant-joined', () => safe(bump));
  callObject.on('left-meeting', () => safe(() => {
    if (!joinedAt) return;
    addonTrack('call_ended', {
      room_hash: hash || cachedRoomHash(roomName) || undefined,
      duration_sec: Math.round((Date.now() - joinedAt) / 1000),
      participants: maxParticipants,
      provider: 'daily',
    });
    joinedAt = 0;
    joiningAt = 0;
  }));
  const onCaptions = () => safe(() => {
    if (captionsSent) return;
    captionsSent = true;
    addonTrack('captions_started', { room_hash: hash || undefined, provider: 'daily' });
  });
  callObject.on('transcription-started', onCaptions);
});
